import {
  TypingIndicator,
  TypingIndicatorsForConversation,
  TypingIndicatorDTO,
} from '../types/index.types';

/**
 * Whether an incoming timestamp should replace the existing typing indicator.
 * Updates when there is no indicator yet, or the existing one is older/equal.
 */
export const shouldUpdateTypingIndicator = (
  existing: TypingIndicator | undefined,
  incomingTimestamp: number
): boolean => {
  return !existing || existing.lastUpdatedAt <= incomingTimestamp;
};

/**
 * Keeps `startedTypingAt` across refreshes while the person is still typing.
 * Starts a new session timestamp when typing begins after stop/expiry/absence.
 */
const withStartedTypingAt = (
  existing: TypingIndicator | undefined,
  incoming: TypingIndicator
): TypingIndicator => {
  const isContinuingTyping =
    existing?.state === 'typing' && incoming.state === 'typing';

  return {
    ...incoming,
    startedTypingAt: isContinuingTyping
      ? existing.startedTypingAt
      : incoming.lastUpdatedAt,
  };
};

/**
 * Merges incoming person typing indicators into the current conversation map,
 * only applying updates when the person has no indicator or an older/equal timestamp.
 */
export const mergeTypingIndicatorsForConversation = (
  current: TypingIndicatorsForConversation | undefined,
  incoming: TypingIndicatorsForConversation
): TypingIndicatorsForConversation => {
  const result = new Map(current);

  for (const [personId, indicator] of incoming) {
    const existing = result.get(personId);

    if (shouldUpdateTypingIndicator(existing, indicator.lastUpdatedAt)) {
      result.set(personId, withStartedTypingAt(existing, indicator));
    }
  }

  return result;
};

/**
 * Applies a conversation fetch's `typing` array onto the current indicators.
 * People in the array are typing. People missing from it are removed unless a
 * newer local update arrived while the request was in flight.
 */
export const applyFetchedTypingForConversation = (
  current: TypingIndicatorsForConversation | undefined,
  typing: TypingIndicatorDTO[]
): TypingIndicatorsForConversation => {
  const incoming = new Map<number, TypingIndicator>();
  let newestIncomingTimestamp = Number.NEGATIVE_INFINITY;

  // Normalize DTOs into a map and track the newest fetch timestamp.
  for (const typer of typing) {
    if (!typer?.personId || !typer.timestamp) {
      continue;
    }

    incoming.set(typer.personId, {
      state: 'typing',
      lastUpdatedAt: typer.timestamp,
      startedTypingAt: typer.timestamp,
    });
    newestIncomingTimestamp = Math.max(
      newestIncomingTimestamp,
      typer.timestamp
    );
  }

  const next = new Map<number, TypingIndicator>();

  if (current) {
    // Keep people missing from the fetch only if a newer local update arrived in-flight.
    for (const [personId, indicator] of current) {
      if (incoming.has(personId)) {
        continue;
      }

      if (
        typing.length > 0 &&
        indicator.lastUpdatedAt > newestIncomingTimestamp
      ) {
        next.set(personId, indicator);
      }
    }
  }

  // Apply fetched typers, preserving startedTypingAt when still typing.
  for (const [personId, indicator] of incoming) {
    const existing = current?.get(personId);

    if (shouldUpdateTypingIndicator(existing, indicator.lastUpdatedAt)) {
      next.set(personId, withStartedTypingAt(existing, indicator));
    } else if (existing) {
      next.set(personId, existing);
    }
  }

  return next;
};
